import React from "react";

// Shared living-sky backdrop for the /manifest sub-site: twinkling stars,
// drifting nebulas, shooting stars, and a crescent moon. Rendered once per
// page behind the content; also carries the keyframes every manifest page
// uses (ms-* classes). Star positions are seeded so the sky is identical on
// every visit — it's *their* sky.

const mulberry32 = (seed) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const rand = mulberry32(20260713);
const STAR_COLORS = ["#ffffff", "#ffffff", "#ffffff", "#fde68a", "#c4b5fd"];
const STARS = Array.from({ length: 110 }, () => ({
  left: rand() * 100,
  top: rand() * 100,
  size: 1 + rand() * 1.7,
  delay: rand() * 7,
  dur: 2.5 + rand() * 4.5,
  color: STAR_COLORS[Math.floor(rand() * STAR_COLORS.length)],
}));

const KEYFRAMES = `
@keyframes ms-twinkle {
  0%, 100% { opacity: 0.12; transform: scale(0.75); }
  50% { opacity: 0.95; transform: scale(1.15); }
}
@keyframes ms-drift {
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50% { transform: translate3d(3%, -4%, 0) scale(1.12); }
}
@keyframes ms-shoot {
  0% { transform: translate3d(0, 0, 0) rotate(-45deg); opacity: 0; }
  2% { opacity: 0.9; }
  9% { transform: translate3d(-44vw, 44vw, 0) rotate(-45deg); opacity: 0; }
  100% { transform: translate3d(-44vw, 44vw, 0) rotate(-45deg); opacity: 0; }
}
@keyframes ms-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-7px); }
}
@keyframes ms-shimmer {
  to { background-position: 200% center; }
}
@keyframes ms-rise {
  0% { transform: translateY(15vh) scale(0.5); opacity: 0; }
  12% { opacity: 1; }
  100% { transform: translateY(-95vh) scale(1.1); opacity: 0; }
}
@keyframes ms-pop {
  0% { transform: scale(0.5); opacity: 0; }
  60% { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
}
.ms-star { position: absolute; border-radius: 9999px; animation: ms-twinkle ease-in-out infinite; }
.ms-neb { position: absolute; border-radius: 9999px; filter: blur(70px); animation: ms-drift ease-in-out infinite; }
.ms-shoot {
  position: absolute; width: 120px; height: 2px; border-radius: 9999px;
  background: linear-gradient(to right, rgba(255,255,255,0.9), transparent);
  opacity: 0; animation: ms-shoot linear infinite;
}
.ms-float { animation: ms-float 5.5s ease-in-out infinite; }
.ms-shimmer {
  background: linear-gradient(110deg, #fef3c7 20%, #fbbf24 35%, #f8f5ff 50%, #c4b5fd 65%, #fef3c7 80%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: ms-shimmer 7s linear infinite;
}
.ms-rise { position: absolute; bottom: -10vh; animation: ms-rise ease-in infinite; }
.ms-pop { animation: ms-pop 0.35s ease-out; }
@media (prefers-reduced-motion: reduce) {
  .ms-star, .ms-neb, .ms-shoot, .ms-float, .ms-shimmer, .ms-rise, .ms-pop { animation: none !important; }
  .ms-shoot, .ms-rise { display: none; }
}
`;

const CosmicSky = () => (
  <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
    <style>{KEYFRAMES}</style>
    {/* Deep-space base wash */}
    <div className="absolute inset-0 bg-[linear-gradient(180deg,#05050e_0%,#0b0a22_45%,#120b26_75%,#05050e_100%)]" />
    {/* Slow-drifting nebulas */}
    <div
      className="ms-neb"
      style={{
        left: "-10%",
        top: "-5%",
        width: "55vw",
        height: "55vw",
        background: "radial-gradient(circle, rgba(124,58,237,0.28), transparent 65%)",
        animationDuration: "26s",
      }}
    />
    <div
      className="ms-neb"
      style={{
        right: "-12%",
        bottom: "-8%",
        width: "60vw",
        height: "60vw",
        background: "radial-gradient(circle, rgba(217,119,6,0.18), transparent 65%)",
        animationDuration: "32s",
        animationDelay: "-9s",
      }}
    />
    <div
      className="ms-neb"
      style={{
        left: "30%",
        top: "45%",
        width: "40vw",
        height: "40vw",
        background: "radial-gradient(circle, rgba(56,189,248,0.10), transparent 65%)",
        animationDuration: "38s",
        animationDelay: "-17s",
      }}
    />
    {/* Twinkling stars */}
    {STARS.map((s, i) => (
      <span
        key={i}
        className="ms-star"
        style={{
          left: `${s.left}%`,
          top: `${s.top}%`,
          width: `${s.size}px`,
          height: `${s.size}px`,
          background: s.color,
          boxShadow: `0 0 ${s.size * 3}px ${s.color}`,
          animationDelay: `${s.delay}s`,
          animationDuration: `${s.dur}s`,
        }}
      />
    ))}
    {/* Occasional shooting stars */}
    <span className="ms-shoot" style={{ right: "5%", top: "12%", animationDuration: "17s", animationDelay: "4s" }} />
    <span className="ms-shoot" style={{ right: "25%", top: "4%", animationDuration: "23s", animationDelay: "13s" }} />
    {/* Crescent moon, gently bobbing */}
    <div className="ms-float absolute right-[7%] top-[9%]">
      <div className="absolute -inset-6 rounded-full bg-amber-200/10 blur-2xl" />
      <div
        className="h-20 w-20 rounded-full sm:h-24 sm:w-24"
        style={{ boxShadow: "inset -17px 7px 0 0 rgba(253,230,138,0.7)" }}
      />
    </div>
  </div>
);

// Page shell: dark canvas + sky, with content layered above it.
export const ManifestShell = ({ children }) => (
  <div className="relative min-h-screen overflow-hidden bg-[#05050e] text-slate-100">
    <CosmicSky />
    <div className="relative z-10">{children}</div>
  </div>
);

export default CosmicSky;
